import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getUnreadNotificationsCount } from '../services/notificationsService';

export const NotificationContext = createContext({
    unreadCount: 0,
    fetchUnreadCount: async () => {},
    decrementCount: () => {},
    resetCount: () => {},
});

export const NotificationProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchUnreadCount = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const data = await getUnreadNotificationsCount();
            setUnreadCount(data?.count ?? 0);
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    }, [isAuthenticated]);

    const decrementCount = useCallback(() => {
        setUnreadCount((prev) => Math.max(0, prev - 1));
    }, []);

    const resetCount = useCallback(() => {
        setUnreadCount(0);
    }, []);

    // Initial fetch when authentication changes
    useEffect(() => {
        if (isAuthenticated) {
            fetchUnreadCount();
        } else {
            setUnreadCount(0);
        }
    }, [isAuthenticated, fetchUnreadCount]);

    // Smart Polling: Fetch unread count every 60 seconds if authenticated and page is visible
    useEffect(() => {
        if (!isAuthenticated) return;

        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchUnreadCount();
            }
        }, 60000);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchUnreadCount();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isAuthenticated, fetchUnreadCount]);

    return (
        <NotificationContext.Provider
            value={{
                unreadCount,
                fetchUnreadCount,
                decrementCount,
                resetCount,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
